import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { useAlarmReport } from '../useAlarmReport';
import { AlarmReportCriteria, AlarmReportResults } from '../AlarmReportContent';
import { formatAlarmInterval } from './alarm-interval';
import { formatEnvQueryGroup } from '../env-query-builder/env-query-format';
import { dataWindowSecondsToValue, dataWindowUnitLabels, formatDataWindowValue, getDataWindowUnit } from '../filterParameterMapping';

export const AlarmRuleReportModal = ({ rule, onClose, alarmRuleReportService, alarmNotificationService, $translate }) => {
  const [requests] = useState(() => {
    const eventFilter = {
      count: 0,
      skip: 0,
      eventSortType: 'ACTIVATED',
      cars: rule.cars || [],
      sortReverse: true,
      searchString: null,
      startDate: null,
      endDate: null,
      subsystemIds: rule.subsystemIds || [],
      eventIds: rule.eventNumbers || [],
      consist: rule.consists?.[0] || null,
      localTimezone: DateTime.local().zoneName,
      environmentDataQuery: rule.environmentDataQuery || null,
    };
    return {
      request: () => alarmRuleReportService.runRuleAsReportPaged(rule.id, {
        occurrence: Number(rule.alarmThresholdCount),
        intervalSeconds: rule.alarmIntervalSeconds,
        dataWindowSeconds: rule.dataWindowSeconds,
        eventFilter,
      }),
      exportCsv: () => alarmRuleReportService.exportRuleReportCsv(rule.id, eventFilter),
      exportExcel: () => alarmRuleReportService.exportRuleReportExcel(rule.id, eventFilter),
    };
  });
  const report = useAlarmReport({
    ...requests,
    service: alarmRuleReportService,
    notifications: alarmNotificationService,
    errorTitle: 'Failed to run alarm rule report',
    exportErrorTitle: 'Failed to export alarm rule report',
  });
  const unit = getDataWindowUnit(rule.dataWindowSeconds);
  const summary = {
    consists: rule.consists,
    cars: rule.cars,
    subsystems: rule.subsystemIds,
    events: rule.eventNumbers,
    environmentQuery: rule.environmentDataQuery ? formatEnvQueryGroup(rule.environmentDataQuery) : '',
  };
  const close = () => { report.cancel(); onClose(); };

  return (
    <div className="alarm-event-view-modal" data-testid="alarm-rule-report-modal">
      <div className="modal-header">
        <h3 className="modal-title">Run Report: {rule.ruleName}</h3>
        <button type="button" className="close" aria-label="close" onClick={close} data-testid="alarm-rule-report-close-button"><span aria-hidden="true">x</span></button>
      </div>
      <div className="modal-body">
        <section className="alarm-report-criteria" data-testid="alarm-rule-report-criteria">
          <h4>Rule Criteria</h4>
          <p className="help-block" data-testid="alarm-rule-report-data-window-help">Rules are evaluated every hour.</p>
          <div className="alarm-report-criteria__grid alarm-report-criteria__grid--inline">
            <AlarmReportCriteria summary={summary}>
              <p><strong> Data Window:</strong> {formatDataWindowValue(dataWindowSecondsToValue(rule.dataWindowSeconds, unit))} {dataWindowUnitLabels[unit]}</p>
              <p><strong>Threshold Count:</strong> {rule.alarmThresholdCount}</p>
              <p><strong>Within:</strong> {formatAlarmInterval(rule.alarmIntervalSeconds)}</p>
            </AlarmReportCriteria>
          </div>
        </section>
        <AlarmReportResults report={report} prefix="alarm-rule-report" translate={$translate} />
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={close} data-testid="alarm-rule-report-close-footer-button"><i className="fas fa-times fa-fw" aria-hidden="true" /> Close</button>
      </div>
    </div>
  );
};

AlarmRuleReportModal.propTypes = {
  rule: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  alarmRuleReportService: PropTypes.object.isRequired,
  alarmNotificationService: PropTypes.object.isRequired,
  $translate: PropTypes.func.isRequired,
};